import { useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md';

import useChangePasswordForm from './useChangePasswordForm';
import PasswordRequirements from './PasswordRequirements';
import TextField from '../UI/TextField';
import Button from '../UI/Button';
import styles from './ChangePasswordForm.module.scss';

interface ChangePasswordFormProps {
  token: string;
  userIdentification?: string | null;
}

const ChangePasswordForm = ({ token, userIdentification }: ChangePasswordFormProps) => {
  const { t } = useTranslation();
  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    onSubmit,
    errors,
    isRequestSent,
    isLoading,
    validationRules,
  } = useChangePasswordForm({ token });

  const togglePasswordVisibility = () => setPasswordVisibility((prev) => !prev);


  const TrailingIcon = useMemo(
    () =>
      passwordVisibility ? (
        <MdOutlineVisibilityOff onClick={togglePasswordVisibility} />
      ) : (
        <MdOutlineVisibility onClick={togglePasswordVisibility} />
      ),
    [passwordVisibility],
  );
  return (
    <>
      {isRequestSent ? (
        <div>
          <p>{t('changePasswordForm.successMessage')}</p>
        </div>
      ) : (
        <>
          <h1 className="h3">{t('changePasswordForm.title')}</h1>
          {userIdentification && (
            <p className="body-small">
              <Trans
                i18nKey="changePasswordForm.enterNewPassword"
                values={{ userIdentification }}
                components={{ strong: <strong /> }}
              />
            </p>
          )}
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.formFields}>
              <TextField
                label={t('changePasswordForm.fields.newPassword')}
                subLabel={<PasswordRequirements validationRules={validationRules} />}
                type={passwordVisibility ? 'text' : 'password'}
                {...register('newPassword')}
                autoComplete="off"
                error={!!errors.newPassword?.message}
                supportingText={errors.newPassword}
                trailingIcon={TrailingIcon}
              />
              <TextField
                label={t('changePasswordForm.fields.confirmPassword')}
                type={passwordVisibility ? 'text' : 'password'}
                {...register('newPasswordDoubleCheck')}
                autoComplete="off"
                error={!!errors.newPasswordDoubleCheck?.message}
                supportingText={errors.newPasswordDoubleCheck}
              />
            </div>
            <div className={styles.formNavigation}>
              <Button type="submit" disabled={isLoading}>{t('changePasswordForm.button.submit')}</Button>
            </div>
            <Button
              type="button"
              className={styles.requestResetLink}
              onClick={() => {
                window.location.href = userIdentification
                  ? `/?prefill=${encodeURIComponent(userIdentification)}`
                  : '/';
              }}
            >
              {t('changePasswordForm.requestNewTokenButton')}
            </Button>
          </form>
        </>
      )}
    </>
  );
};

export default ChangePasswordForm;
